// ===== Packages =====

import styled, { css }      from 'styled-components';

// ===== Services =====

import {
    hexToRgb,
    setColorLightness,
}                           from '../../services';

// ===== Enums =====

import {
    ZOOM_LEVEL,
}                           from '../Editor/elements/enums';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    BUTTON_ICON_MULTIPLIER,
    CURSOR_Z_INDEX,
    POST_BANNER_Z_INDEX,
    POST_TITLE_MARGIN_BOTTOM,
    POST_TITLE_MARGIN_TOP,
    POST_SUBTITLE_MARGIN,
    POST_AUTHOR_MARGIN_LEFT,
    POST_DETAIL_TEXT_MARGIN_LEFT,
    POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT,
    POST_CONTENT_LEFT_CONTAINER_WIDTH_MEDIUM,
    POST_CONTENT_LEFT_CONTAINER_PADDING_RIGHT_MEDIUM,
    POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT_SMALL,
    POST_CONTENT_LEFT_CONTAINER_PADDING_RIGHT_SMALL,
    POST_CONTENT_RIGHT_BANNER_CONTAINER_WIDTH_SUPER_SMALL,
    POST_THUMBNAIL_MAX_WIDTH_MEDIUM,
    POST_THUMBNAIL_MAX_WIDTH_SMALL,
    SUCCESS_BACKGROUND_LIGHTNESS_VALUE,
    ERROR_BACKGROUND_LIGHTNESS_VALUE,
    MODAL_SHEET_CLOSE_BUTTON_CONTAINER_TOP,
    MODAL_SHEET_CLOSE_BUTTON_LENGTH,
    POST_PAYWALL_Z_INDEX,
    DEFAULT_FONT_SIZE,
    READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER,
    READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER,
    POST_AUTHOR_THRESHOLD,
    READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER_SMALL,
    POST_SUPER_SMALL_VIEWPORT_THRESHOLD,
    POST_BANNER_PURCHASE_ACTION_GAP,
    POST_PAYWALL_OVERHANG_HEIGHT,
    STICKY_PURCHASE_CTA_Z_INDEX,
    STICKY_PURCHASE_CTA_MAX_WIDTH,
    ANNOTATION_TOGGLE_Z_INDEX,
    ZOOM_LEVEL_SELECTOR_Z_INDEX,
    RESUME_READER_POSITION_BUTTON_Z_INDEX,
    POST_HISTORY_BUTTON_Z_INDEX,
    DETAIL_SHEET_HEIGHT_REDUCTION,
}                           from '../../constants/generalConstants';
import FONT_TYPE            from '../../constants/fontType';

interface ContainerProps {
    hasSuccess: boolean,
    hasError: boolean,
    isSteering: boolean,
    useDocumentScroll: boolean,
}
export const Container = styled.div<ContainerProps>`
    width: 100%;
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    ${({ useDocumentScroll }) => (useDocumentScroll ? css`
        height: auto;
        min-height: 100vh;
        min-height: 100svh;
        min-height: 100dvh;
    ` : '')}
    background-color: ${({
        hasSuccess,
        hasError,
        isSteering,
        theme,
    }) => {
        if (hasError) {
            return setColorLightness(
                theme.color.error,
                ERROR_BACKGROUND_LIGHTNESS_VALUE,
            );
        }

        if (hasSuccess) {
            return setColorLightness(
                theme.verascopeColor.blue200,
                SUCCESS_BACKGROUND_LIGHTNESS_VALUE,
            );
        }

        if (isSteering) {
            return theme.verascopeColor.purple500;
        }

        return theme.color.cream500;
    }};
`;

interface PostContentContainerProps {
    isSteering: boolean,
    showWarning: boolean,
    warningBackground: string,
    transitionDuration: number,
    useDocumentScroll: boolean,
    useReaderSelectionHighlightStyling: boolean,
}
export const PostContentContainer = styled.div<PostContentContainerProps>`
    position: relative;
    width: 100%;
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    ${({ useDocumentScroll }) => (useDocumentScroll ? css`
        height: auto;
        min-height: 100vh;
        min-height: 100svh;
        min-height: 100dvh;
    ` : '')}
    // Keep post-side warning color on the post container only; annotation-side
    // warning color is owned by DetailView to avoid cross-pane color bleed.
    background: ${({ showWarning, warningBackground }) => (showWarning
        ? warningBackground
        : 'inherit')};
    overflow-y: ${({ useDocumentScroll }) => (useDocumentScroll ? 'visible' : 'scroll')};
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch; /* smooth scrolling for iOS */
    /* Keep reader-owned corrections instant instead of inheriting a smoother
     * scroll path during auth-lock and progressive-read-only adjustments.
     */
    scroll-behavior: auto;
    touch-action: pan-y;
    /* Disable native scroll anchoring on the reader surface so read-only chunk
     * growth does not fight the manual scroll lock in PostEditor.
     */
    overflow-anchor: none;
    ${({ useReaderSelectionHighlightStyling, theme }) => (useReaderSelectionHighlightStyling ? css`
        &::selection,
        & *::selection {
            background: ${theme.verascopeColor.orange200};
            color: ${theme.color.white};
        }
        &::-moz-selection,
        & *::-moz-selection {
            background: ${theme.verascopeColor.orange200};
            color: ${theme.color.white};
        }
    ` : '')}
    transition: ${({
        theme,
        isSteering,
        transitionDuration,
    }) => `background ${isSteering ? 0 : transitionDuration}ms ${theme.motion.eagerEasing}${isSteering
        ? ''
        : `, width ${transitionDuration}ms ${theme.motion.overshoot}`}`};
`;

/**
 * Keeps the detached-annotation repair lane vertically scrollable without
 * fighting the main reader layout.
 */
export const DetachedAnnotationShelfLayout = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
`;

interface DetachedAnnotationsPanelContainerProps {
    isReattachActive?: boolean,
    pinOffsetY?: number,
    animateToPinnedCenter?: boolean,
    width?: number,
}
export const DetachedAnnotationsPanelContainer = styled.div<DetachedAnnotationsPanelContainerProps>`
    display: flex;
    flex-direction: column;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
    box-sizing: border-box;
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    padding-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin-top: ${({ isReattachActive }) => (isReattachActive ? '0px' : `${0.5 * DEFAULT_FONT_SIZE}px`)};
    background: ${({ theme }) => theme.verascopeColor.purple200};
    border-top-left-radius: ${`${0.25 * DEFAULT_FONT_SIZE}px`};
    border-bottom-left-radius: ${`${0.25 * DEFAULT_FONT_SIZE}px`};
    border-bottom-right-radius: ${`${0.25 * DEFAULT_FONT_SIZE}px`};
    box-shadow: ${({ theme }) => theme.color.boxShadow300};
    will-change: ${({ isReattachActive }) => (isReattachActive ? 'transform' : 'auto')};
    position: ${({ isReattachActive }) => (isReattachActive ? 'fixed' : 'relative')};
    top: ${({ isReattachActive }) => (isReattachActive ? '50%' : 'auto')};
    right: ${({ isReattachActive }) => (isReattachActive ? '0px' : 'auto')};
    width: ${({ isReattachActive, width }) => (isReattachActive && width != null ? `${width}px` : '100%')};
    max-height: ${({ isReattachActive }) => (isReattachActive
        ? `calc(100dvh - ${2 * BODY_FONT_SIZE}px)`
        : 'none')};
    transform: ${({ isReattachActive, pinOffsetY }) => (isReattachActive
        ? `translateY(calc(-50% + ${pinOffsetY || 0}px))`
        : 'none')};
    z-index: ${({ isReattachActive }) => (isReattachActive ? POST_HISTORY_BUTTON_Z_INDEX - 1 : 'auto')};
    transition: ${({ animateToPinnedCenter, theme }) => (animateToPinnedCenter
        ? `transform 240ms ${theme.motion.eagerEasing}`
        : 'none')};
`;

export const DetachedAnnotationsPanelHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

export const DetachedAnnotationsPanelTitle = styled.h3`
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.9em;
    font-weight: 700;
    color: ${({ theme }) => theme.color.white};
`;

export const DetachedAnnotationsPanelDescription = styled.p`
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.75em;
    line-height: 1.4;
    color: ${({ theme }) => theme.color.cream400};
`;

interface DetachedAnnotationsListProps {
    isReattachActive?: boolean,
}
export const DetachedAnnotationsList = styled.div<DetachedAnnotationsListProps>`
    display: flex;
    flex-direction: column;
    gap: ${`${0.625 * BODY_FONT_SIZE}px`};
    min-height: 0;
    overflow-y: ${({ isReattachActive }) => (isReattachActive ? 'auto' : 'visible')};
`;

interface PostBannerProps {
    height: number,
    stickyPostBannerActive: boolean,
    transitionDuration: number,
}
export const PostBanner = styled.div<PostBannerProps>`
    position: relative; // Modified directly using ref in ReaderView
    top: auto; // Modified directly using ref in ReaderView
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-end;
    background: ${({ stickyPostBannerActive, theme }) => (stickyPostBannerActive
        ? `rgba(${
            hexToRgb(theme.verascopeColor.purple200)!.r
        }, ${
            hexToRgb(theme.verascopeColor.purple200)!.g
        }, ${
            hexToRgb(theme.verascopeColor.purple200)!.b
        }, 0.0)`
        : theme.color.cream100
    )};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    width: 100%;
    height: ${({ height }) => `${height}px`};
    transform: none; // Modified directly using ref in ReaderView
    z-index: ${POST_BANNER_Z_INDEX};
    transition: ${({ theme, transitionDuration }) => `
        background ${transitionDuration}ms ${theme.motion.eagerEasing},
        top ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
    ${() => (() => {
        if (CSS.supports('backdrop-filter: blur(5px)')) {
            return 'backdrop-filter: blur(5px);';
        }
    })()};

    ${({ theme }) => theme.mediaQuery.medium} {
        justify-content: flex-start;
    }

    ${({ theme }) => theme.mediaQuery.small} {
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
    }
`;

interface PostBannerLeftContainerProps {
    widthExpanded: number,
    widthContracted: number,
    heightExpanded: number,
    paddingExpanded: number,
    transitionDuration: number,
}
export const PostBannerLeftContainer = styled.div<PostBannerLeftContainerProps>`
    position: relative;
    display: flex;
    box-sizing: border-box;
    justify-content: flex-end;
    align-items: center;
    width: ${({ widthExpanded, widthContracted }) => `calc(${widthExpanded}px + (${widthContracted - widthExpanded}px * var(--reader-post-banner-left-collapse-progress, 0)))`};
    height: ${({ heightExpanded }) => `var(--reader-post-banner-left-height, ${heightExpanded}px)`};
    padding: ${({ paddingExpanded }) => `var(--reader-post-banner-left-padding, ${paddingExpanded}px)`};
    padding-left: ${`${POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT}px`};
    padding-right: ${({ paddingExpanded }) => `var(--reader-post-banner-left-padding, ${paddingExpanded}px)`};
    transition: ${({ transitionDuration, theme }) => `
        width ${transitionDuration}ms ${theme.motion.delayEasing}
    `};

    ${({ theme }) => theme.mediaQuery.large} {
        /* Sticky title and action rows must not squeeze the cover sideways. */
        flex-shrink: 0;
    }

    ${({ theme }) => theme.mediaQuery.medium} {
        width: ${`${POST_CONTENT_LEFT_CONTAINER_WIDTH_MEDIUM}px`};
        padding-right: ${`${POST_CONTENT_LEFT_CONTAINER_PADDING_RIGHT_MEDIUM}px`};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        flex: 1;
        width: 100%;
        height: auto;
        padding-left: ${`${POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT_SMALL}px`};
        padding-right: ${`${POST_CONTENT_LEFT_CONTAINER_PADDING_RIGHT_SMALL}px`};
        justify-content: center;
        align-items: flex-end;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex: 1;
        width: 100%;
        height: auto;
        padding-left: ${`${POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT_SMALL}px`};
        padding-right: ${`${POST_CONTENT_LEFT_CONTAINER_PADDING_RIGHT_SMALL}px`};
        justify-content: center;
        align-items: flex-end;
    }
`;

interface PostBannerCenterContainerProps {
    height: number;
    postBannerLeftContainerWidth: number,
    postBannerRightContainerWidth: number,
    pageLoadRendersCompleted: boolean,
}
export const PostBannerCenterContainer = styled.div<PostBannerCenterContainerProps>`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: relative;
    box-sizing: border-box;
    min-width: 0;
    height: ${({ height }) => `${height}px`};
    /* margin: 0 auto; */
    padding-left: ${`${2 * DEFAULT_FONT_SIZE}px`};

    ${() => `@media (min-width: 1600px) and (min-aspect-ratio: 21/9) {
        margin-left: auto;
        margin-right: auto;
        align-items: center;
        padding-left: 0;
        padding-right: 0;
    }`}
    ${({ postBannerLeftContainerWidth, postBannerRightContainerWidth }) => `@media (min-width: 1600px) and (min-aspect-ratio: 21/9) {
        transform: translateX(${(postBannerRightContainerWidth - postBannerLeftContainerWidth) / 2}px);
    }`}

    ${({ theme }) => theme.mediaQuery.medium} {
        width: 100%;
        height: ${({ height }) => `${height}px`};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        width: 100%;
        height: auto;
        padding-left: ${`${1 * BODY_FONT_SIZE}px`};
        padding-right: ${`${1 * BODY_FONT_SIZE}px`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: 100%;
        height: auto;
        padding-left: ${`${1 * BODY_FONT_SIZE}px`};
        padding-right: ${`${1 * BODY_FONT_SIZE}px`};
    }

    ${() => `@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        height: auto;
        width: 100%;
        padding-left: 0;
        padding: ${`0 ${1 * BODY_FONT_SIZE}px`};
    }`}
`;

interface PostBannerRightContainerProps {
    annotationViewWidth: number,
}
export const PostBannerRightContainer = styled.div<PostBannerRightContainerProps>`
    position: relative;
    width: ${({ annotationViewWidth }) => `${annotationViewWidth}px`};

    ${`@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        width: ${`${POST_CONTENT_RIGHT_BANNER_CONTAINER_WIDTH_SUPER_SMALL}px`};
    }`}

    ${({ theme }) => theme.mediaQuery.small} {
        width: 100%;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: 100%;
    }
`;

interface PostContentProps {
    adjustMarginTop: number,
}
export const PostContent = styled.div<PostContentProps>`
    position: relative;
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: ${({ adjustMarginTop }) => `${adjustMarginTop}px`};
    background: inherit;
`;

interface PostContentLeftContainerProps {
    width: number,
}
export const PostContentLeftContainer = styled.div<PostContentLeftContainerProps>`
    position: relative;
    width: ${({ width }) => `${width}px`};
    height: fit-content;
`;

interface PostContentCenterContainerProps {
    readOnly: boolean,
    postContentLeftContainerWidth: number,
    postContentRightContainerWidth: number,
}
export const PostContentCenterContainer = styled.div<PostContentCenterContainerProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    min-width: 0;
    will-change: width;
    background: inherit;
    width: ${({
        postContentLeftContainerWidth,
        postContentRightContainerWidth,
    }) => `calc(100% - (${postContentLeftContainerWidth}px + ${postContentRightContainerWidth}px))`};
    height: fit-content;
    // Commented out because it interferes with Zoom Hover Card presentation (gets truncated)
    /* overflow: ${({ readOnly }) => (readOnly ? 'hidden' : 'visible')}; */
`;

interface PostThumbnailProps {
    url?: string,
    stickyPostBannerActive: boolean,
    bottomExpanded: number,
    widthExpanded: number,
    widthContracted: number,
    heightContracted: number,
    heightExpandedLarge: number,
    heightExpandedMedium: number,
    heightExpandedSmall: number,
    heightExpandedExtraSmall: number,
    borderRadiusExpanded: number,
    borderRadiusContracted: number,
}
// A cover remounted after a breakpoint change needs sticky fallback dimensions
// immediately, before the next scroll event supplies its CSS variables.
export const PostThumbnail = styled.div<PostThumbnailProps>`
    position: relative;
    bottom: ${({ bottomExpanded }) => `var(--reader-post-thumbnail-bottom, ${bottomExpanded}px)`};
    width: ${({ widthExpanded, widthContracted, stickyPostBannerActive }) => `var(--reader-post-thumbnail-width, ${stickyPostBannerActive ? widthContracted : widthExpanded}px)`};
    height: ${({ heightExpandedLarge, heightContracted, stickyPostBannerActive }) => `var(--reader-post-thumbnail-height, ${stickyPostBannerActive ? heightContracted : heightExpandedLarge}px)`};
    background-color: ${({ theme }) => theme.verascopeColor.purple200};
    background-image: ${({ url }) => (url ? `url(${url})` : 'none')};
    background-repeat: no-repeat;
    background-size: cover; // can't use 'contain' because width and height switch as dominant aspect
    background-position: center;
    border-radius: ${({ borderRadiusExpanded, borderRadiusContracted, stickyPostBannerActive }) => `var(--reader-post-thumbnail-border-radius, ${stickyPostBannerActive ? borderRadiusContracted : borderRadiusExpanded}px)`};
    box-shadow: ${({ theme }) => theme.color.boxShadow500};
    overflow: hidden;

    ${({ theme }) => theme.mediaQuery.medium} {
        max-width: ${`${POST_THUMBNAIL_MAX_WIDTH_MEDIUM}px`};
        height: ${({ heightExpandedMedium, heightContracted, stickyPostBannerActive }) => `var(--reader-post-thumbnail-height, ${stickyPostBannerActive ? heightContracted : heightExpandedMedium}px)`};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        max-width: ${`${POST_THUMBNAIL_MAX_WIDTH_SMALL}px`};
        height: ${({ heightExpandedSmall, heightContracted, stickyPostBannerActive }) => `var(--reader-post-thumbnail-height, ${stickyPostBannerActive ? heightContracted : heightExpandedSmall}px)`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        max-width: ${`${POST_THUMBNAIL_MAX_WIDTH_SMALL}px`};
        height: ${({ heightExpandedExtraSmall, heightContracted, stickyPostBannerActive }) => `var(--reader-post-thumbnail-height, ${stickyPostBannerActive ? heightContracted : heightExpandedExtraSmall}px)`};
    }
`;

interface AddPostThumbnailContainerProps {
    isDragAccept: boolean,
    isDragReject: boolean,
}
export const AddPostThumbnailContainer = styled.div<AddPostThumbnailContainerProps>`
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${({
        isDragAccept,
        isDragReject,
        theme,
    }) => {
        if (isDragReject) {
            return theme.color.error;
        }

        if (isDragAccept) {
            return theme.verascopeColor.blue200;
        }

        return 'transparent';
    }};
`;

export const AddPostThumbnailInput = styled.input``;

interface AddPostThumbnailIconProps {
    stickyPostBannerActive: boolean,
    transitionDuration: number,
}
export const AddPostThumbnailIcon = styled.div<AddPostThumbnailIconProps>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: ${({ stickyPostBannerActive }) => `${stickyPostBannerActive ? 1.875 * BODY_FONT_SIZE : 3.125 * BODY_FONT_SIZE}px`};
    width: ${({ stickyPostBannerActive }) => `${stickyPostBannerActive ? 1.875 * BODY_FONT_SIZE : 3.125 * BODY_FONT_SIZE}px`};
    pointer-events: none;
    transition: ${({ transitionDuration, theme }) => `
        height ${transitionDuration}ms ${theme.motion.standardEasing},
        width ${transitionDuration}ms ${theme.motion.standardEasing}
    `};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        height: 100%;
        width: 100%;
        fill: ${({ theme }) => theme.color.white};
    }
`;

export const PostThumbnailUploadProgressBarContainer = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${`${5 * BODY_FONT_SIZE}px`};
    height: ${`${0.625 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    overflow: hidden;
    border: ${({ theme }) => `${0.0625 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple300}`};
`;

interface PostThumbnailUploadProgressBarProps {
    progress: number,
}
export const PostThumbnailUploadProgressBar = styled.div<PostThumbnailUploadProgressBarProps>`
    width: ${({ progress }) => `${progress}%`};
    height: 100%;
    background: ${({ theme }) => theme.verascopeColor.purple500};
`;

export const PostTitlesContainer = styled.div`
    flex: 0; // Any extra space from PostBannerCenterContainer should go to PostMetadataContainer
    position: relative;
    display: flex;
    flex-direction: column;

    ${({ theme }) => theme.mediaQuery.small} {
        flex: 0;
        align-items: center;
        justify-content: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex: 0;
        align-items: center;
        justify-content: center;
    }
    
    ${`@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        flex: 0;
        align-items: center;
        justify-content: center;
    }`}
`;

interface PostTitleProps {
    fontMultiplier: number,
}
export const PostTitle = styled.h1<PostTitleProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 800;
    color: ${({ theme }) => theme.color.white};
    max-width: 35ch;
    margin-top: ${`${POST_TITLE_MARGIN_TOP}px`};
    margin-bottom: ${`${POST_TITLE_MARGIN_BOTTOM}px`};

    ${({ theme }) => theme.mediaQuery.medium} {
        font-size: ${`${(READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) ** 2.8 * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER}em`};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${(READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) ** 2 * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER}em`};
        text-align: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${(READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) ** 1 * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER_SMALL}em`};
        margin-bottom: 0px;
        text-align: center;
    }
`;

interface PostSubtitleProps {
    fontMultiplier: number,
    fixedToViewport: boolean,
    transitionDuration: number,
}
export const PostSubtitle = styled.h2<PostSubtitleProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 400;
    color: ${({ theme }) => theme.color.cream400};
    max-width: 40ch;
    margin: ${POST_SUBTITLE_MARGIN};

    ${({ theme }) => theme.mediaQuery.medium} {
        font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
        text-align: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.9em;
        text-align: center;
    }

    ${({ fixedToViewport, transitionDuration, theme }) => `@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        text-align: center;
        opacity: ${(fixedToViewport ? 0 : 1)} !important;
        transition: opacity ${transitionDuration}ms ${theme.motion.eagerEasing};
    }`}
`;

interface PostInputProps {
    isInvalid?: boolean,
    isItalic?: boolean,
    color: string,
    placeholderColor?: string,
    placeholderFontSize?: number,
    fontSize: number,
    fontWeight: number,
    marginTop: number,
    customCursorActive: boolean,
}
export const PostInput = styled.input<PostInputProps>`
    display: block;
    position: relative;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontSize }) => `${fontSize}em`};
    font-weight: ${({ fontWeight }) => fontWeight};
    font-style: ${({ isItalic }) => (isItalic ? 'italic' : 'normal')};
    line-height: 1.5em;
    color: ${({ isInvalid, theme, color }) => (isInvalid
        ? theme.verascopeColor.red200
        : color
    )};
    text-align: center;
    background-color: ${({
        isInvalid,
        theme,
    }) => {
        if (isInvalid) {
            return setColorLightness(
                theme.color.error,
                95,
            );
        }

        return setColorLightness(
            theme.verascopeColor.purple300,
            80,
        );
    }};
    border: ${({ theme, isInvalid }) => `${0.125 * BODY_FONT_SIZE}px solid ${isInvalid
        ? theme.verascopeColor.red200
        : theme.color.white}`}; 
    width: 100%;
    margin-top: ${({ marginTop }) => `${marginTop}px`};
    padding: ${`${0.15625 * BODY_FONT_SIZE}px ${0.9375 * BODY_FONT_SIZE}px`};
    text-transform: none;
    border: none;
    text-decoration: none;
    border-radius: ${`${1.875 * BODY_FONT_SIZE}px`};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'text')};

    &:focus{
        outline: none;
    }

    &::placeholder {
        font-size: ${({ placeholderFontSize }) => (placeholderFontSize ? `${placeholderFontSize}px` : 'inherit')};
        color: ${({ theme, placeholderColor }) => placeholderColor || theme.verascopeColor.purple500};
    }
`;

interface PostMetadataContainerProps {
    padding: number,
}
export const PostMetadataContainer = styled.div<PostMetadataContainerProps>`
    flex: 1; // Any extra space from PostBannerCenterContainer should go to this styled component
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    padding-bottom: ${({ padding }) => `${padding}px`};

    ${({ theme }) => theme.mediaQuery.small} {
        flex: 0; // Any extra space from PostBannerCenterContainer should go to PostTitlesContainer
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex: 0; // Any extra space from PostBannerCenterContainer should go to PostTitlesContainer
    }

    ${`@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        flex: 0; // Any extra space from PostBannerCenterContainer should go to PostTitlesContainer
    }`}
`;

interface PostMetadataContentContainerProps {
    padding: number,
}
export const PostMetadataContentContainer = styled.div<PostMetadataContentContainerProps>`
    display: flex;
    flex-direction: row;
    width: 100%;
    padding-top: ${({ padding }) => `${padding}px`};

    ${`@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        padding-top: ${`${0.3125 * BODY_FONT_SIZE}px`};
    }`}
`;

export const PostAuthorContainer = styled.div`
    flex: 0; // Any extra space from PostMetadataContainer should go to PostDetailsContainer
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;

interface PostAuthorNameProps {
    avatarHeight: number,
    fontMultiplier: number,
}
export const PostAuthorName = styled.h4<PostAuthorNameProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 600;
    color: ${({ theme }) => theme.color.white};
    margin: auto 0;
    margin-top: ${({ avatarHeight }) => `${avatarHeight / 2}px`};
    margin-left: ${`${POST_AUTHOR_MARGIN_LEFT}px`};
    white-space: nowrap;
    transform: translateY(-50%);

    @media (max-width: 1420px) {
        display: none;
    }
`;

export const PostDetailsContainer = styled.div`
    flex: 1; // Any extra space from PostMetadataContainer should go to this styled component
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-end;

    ${`@media (max-width: ${POST_AUTHOR_THRESHOLD}px) {
        justify-content: flex-start;
    }`}

    ${({ theme }) => theme.mediaQuery.medium} {
        justify-content: flex-start;
    }

    ${({ theme }) => theme.mediaQuery.small} {
        justify-content: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        justify-content: center;
    }

    ${`@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        justify-content: center;
    }`}
`;

interface PostDetailProps {
    isPurchaseBookButtons?: boolean,
    center?: boolean,
}
export const PostDetail = styled.div<PostDetailProps>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: ${({ center }) => (center ? 'center' : 'flex-start')};
    margin: auto 0;
    margin-top: 0;
    margin-left: ${`${1.25 * BODY_FONT_SIZE}px`};

    ${({ isPurchaseBookButtons }) => isPurchaseBookButtons && css`

        ${`@media (max-width: ${POST_AUTHOR_THRESHOLD}px) {
            margin: 0;
        }`}

        ${({ theme }) => theme.mediaQuery.medium} {
            margin: 0;
        }

        ${({ theme }) => theme.mediaQuery.small} {
            margin: 0;
        }

        ${({ theme }) => theme.mediaQuery.extraSmall} {
            margin: 0;
        }

        @media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
            margin: 0;
        }
    `}
`;

export const PostDetailIcon = styled.div`
    height: ${`${1.4 * BODY_FONT_SIZE}px`};
    width: ${`${1.4 * BODY_FONT_SIZE}px`};
    min-width: ${`${1.4 * BODY_FONT_SIZE}px`};
    
    & svg, & div {
        position: relative;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    & path {
        fill: ${({ theme }) => theme.color.cream300};
    }
`;

interface PostDetailTextProps {
    avatarHeight: number,
    fontMultiplier: number,
}
export const PostDetailText = styled.h4<PostDetailTextProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 600;
    color: ${({ theme }) => theme.color.white};
    margin: 0;
    margin-top: ${({ avatarHeight }) => `${avatarHeight / 2}px`};
    margin-left: ${`${POST_DETAIL_TEXT_MARGIN_LEFT}px`};
    transform: translateY(-50%);
`;

interface CollapsedPostTitle {
    height: number,
}
export const CollapsedPostTitle = styled.h4<CollapsedPostTitle>`
    position: absolute;
    left: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1em;
    font-weight: 700;
    color: ${({ theme }) => theme.verascopeColor.purple200};
    max-width: 35ch;
    height: ${({ height }) => `${height}px`};
    text-overflow: ellipsis;
    margin: 0;
    margin-left: ${`${1 * DEFAULT_FONT_SIZE}px`};

    ${`@media (max-width: ${POST_SUPER_SMALL_VIEWPORT_THRESHOLD}px) {
        justify-content: center;
        max-width: none;
        width: 100%;
    }`}
`;

export const AnnotationContainer = styled.div`
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    overflow-x: clip; // This prevents the viewport from having extra width causing viewport scroll
    overflow-y: visible; // Keep annotation bucket top offsets anchored to the reader scroll surface
`;

export const UploadProgressContainer = styled.div`
    position: fixed;
    display: block;
    width: ${`${10.3125 * BODY_FONT_SIZE}px`};
    bottom: ${`${7.5 * BODY_FONT_SIZE}px`};
    right: ${`${2.5 * BODY_FONT_SIZE}px`};
    z-index: ${CURSOR_Z_INDEX - 1};
`;

export const PostUpdateTextContainer = styled.div`
    display: flex;
    align-items: center;

    & > div {
        margin-bottom: 0px;
    }
`;

export const EditorUpdateText = styled.div`
    position: relative;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.9em;
    font-weight: 400;
    color: ${({ theme }) => theme.color.neutral600};
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
    text-align: center;
    user-select: none;
`;

export const ModalSheetCloseButtonContainer = styled.div`
    position: absolute;
    top: ${`${MODAL_SHEET_CLOSE_BUTTON_CONTAINER_TOP}px`};
    right: ${`${0.9375 * BODY_FONT_SIZE}px`};
    z-index: 1;
    background: ${({ theme }) => theme.color.white};
    border-radius: ${`${MODAL_SHEET_CLOSE_BUTTON_LENGTH / 2}px`};
`;

interface ZoomLevelSelectorContainerProps {
    readOnly: boolean,
    selectorWidth: number,
    readOnlyRightOffset: number,
}
export const ZoomLevelSelectorContainer = styled.div<ZoomLevelSelectorContainerProps>`
    position: fixed;
    bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    left: ${({ readOnly }) => (readOnly ? 'auto' : '30px')};
    // In read mode this offset is adjusted at runtime to sit left of the annotation toggle button.
    right: ${({ readOnly, readOnlyRightOffset }) => (readOnly ? `${readOnlyRightOffset}px` : 'auto')};
    border-radius: ${({ selectorWidth }) => `${selectorWidth / 2}px`};
    z-index: ${ZOOM_LEVEL_SELECTOR_Z_INDEX};

`;

interface AnnotationVisibilityButtonContainerProps {
    bottom: number,
    right: number,
    background: string,
}
export const AnnotationVisibilityButtonContainer = styled.div<AnnotationVisibilityButtonContainerProps>`
    position: fixed;
    bottom: ${({ bottom }) => `${bottom}px`};
    right: ${({ right }) => `${right}px`};
    // Stays below cursor overlays and above content so the toggle remains reachable.
    z-index: ${ANNOTATION_TOGGLE_Z_INDEX};
    background: ${({ background }) => background};
    border-radius: 50%;
`;

interface ResumeReaderPositionButtonContainerProps {
    bottom: number,
    right: number,
    transitionDuration: number,
}
export const ResumeReaderPositionButtonContainer = styled.div<ResumeReaderPositionButtonContainerProps>`
    position: fixed;
    bottom: ${({ bottom }) => `${bottom}px`};
    right: ${({ right }) => `${right}px`};
    z-index: ${RESUME_READER_POSITION_BUTTON_Z_INDEX};
    transition: ${({ theme, transitionDuration }) => `background ${transitionDuration}ms ${theme.motion.overshoot}`};
`;

export const InternalPostLinkFloatingStatusPill = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${`${0.5 * BODY_FONT_SIZE}px`};
    min-height: ${`${2 * BODY_FONT_SIZE}px`};
    padding: ${`0px ${0.875 * BODY_FONT_SIZE}px 0px ${0.5 * BODY_FONT_SIZE}px`};
    border-radius: ${`${BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.verascopeColor.purple200};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    color: ${({ theme }) => theme.color.white};
    white-space: nowrap;
`;

export const InternalPostLinkFloatingStatusLabel = styled.span`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.8 * BODY_FONT_SIZE}px`};
    font-weight: 500;
    line-height: 1;
`;

export const InternalPostLinkFloatingStatusSpinner = styled.img`
    width: ${`${0.8 * BODY_FONT_SIZE}px`};
    height: ${`${0.8 * BODY_FONT_SIZE}px`};
    animation: rotate 2s linear infinite;
    flex: 0 0 auto;
`;

interface FloatingAudioPlayerContainerProps {
    bottom: number,
    right: number,
}
export const FloatingAudioPlayerContainer = styled.div<FloatingAudioPlayerContainerProps>`
    position: fixed;
    bottom: ${({ bottom }) => `${bottom}px`};
    right: ${({ right }) => `${right}px`};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${`${0.375 * BODY_FONT_SIZE}px`};
    min-width: ${`${6.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.5 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.875 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    z-index: ${RESUME_READER_POSITION_BUTTON_Z_INDEX + 1};
`;

export const FloatingAudioPlayerTransportRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${`${0.375 * BODY_FONT_SIZE}px`};
`;

export const FloatingAudioPlayerTime = styled.div`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.72em;
    font-weight: 600;
    line-height: 1.2;
    color: ${({ theme }) => theme.color.neutral700};
    text-align: center;
    white-space: nowrap;
`;

interface CurrentZoomLevelTextProps {
    currentZoomDigit: ZOOM_LEVEL,
}
export const CurrentZoomLevelText = styled.span<CurrentZoomLevelTextProps>`
    position: absolute;
    top: ${`${1.53125 * BODY_FONT_SIZE}px`};
    left: ${({ currentZoomDigit }) => {
        switch (currentZoomDigit) {
        case ZOOM_LEVEL.five:
            return `${1.73125 * BODY_FONT_SIZE}px`;
        case ZOOM_LEVEL.four:
            return `${1.69375 * BODY_FONT_SIZE}px`;
        case ZOOM_LEVEL.three:
            return `${1.71875 * BODY_FONT_SIZE}px`;
        case ZOOM_LEVEL.two:
            return `${1.71875 * BODY_FONT_SIZE}px`;
        case ZOOM_LEVEL.one:
            return `${1.78125 * BODY_FONT_SIZE}px`;
        default:
            return `${1.69375 * BODY_FONT_SIZE}px`;
        }
    }};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.9em;
    font-weight: 700;
    color: ${({ theme }) => theme.verascopeColor.purple300};
    text-align: center;
    line-height: 1em;
    z-index: ${CURSOR_Z_INDEX - 1};
    pointer-events: none;
`;

export const CurrentZoomLevelIcon = styled.img`
    position: absolute;
    top: ${`${1.5 * BODY_FONT_SIZE}px`};
    left: ${`${1.53125 * BODY_FONT_SIZE}px`};
    width: ${`${0.9375 * BODY_FONT_SIZE}px`};
    height: ${`${0.9375 * BODY_FONT_SIZE}px`};
    pointer-events: none;
`;

interface PostPlaceholderContainerProps {
    postBannerHeight: number,
    postContentMarginTop: number,
}
export const PostPlaceholderContainer = styled.div<PostPlaceholderContainerProps>`
    height: ${({ postBannerHeight, postContentMarginTop }) => `calc(100vh - ${postBannerHeight + postContentMarginTop}px)`};
    height: ${({ postBannerHeight, postContentMarginTop }) => `calc(100svh - ${postBannerHeight + postContentMarginTop}px)`};
    height: ${({ postBannerHeight, postContentMarginTop }) => `calc(100dvh - ${postBannerHeight + postContentMarginTop}px)`};
    width: 100%;

    & > * {
        margin-left: auto !important;
        margin-right: auto !important;
    }
`;

interface HandlebarProps {
    width: number,
    height: number,
    isSteering: boolean,
    transitionDuration: number,
}
export const Handlebar = styled.div<HandlebarProps>`
    position: fixed;
    top: 50%;
    // Keep the resize affordance above decorative post headings when the
    // annotation shelf overlaps the content column.
    z-index: 2;
    transform: translateY(-50%);
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    border-radius: ${({ width }) => `${width / 2}px`};
    background: ${({ isSteering, theme }) => (isSteering ? theme.verascopeColor.orange200 : theme.color.cream400)};
    transition: ${({ theme, transitionDuration }) => `background ${transitionDuration}ms ${theme.motion.eagerEasing}`};

    ${({ theme }) => theme.mediaQuery.small} {
        background: none;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        background: none;
    }
`;

interface HandlebarContainerProps {
    width: number,
    height?: string,
    customCursorActive: boolean,
    nativeCursor: string,
    isSteering: boolean,
    transitionDuration: number,
}
export const HandlebarContainer = styled.div<HandlebarContainerProps>`
    position: absolute;
    top: 0;
    left: 0px;
    // Match the handlebar stacking level so the full grab target stays
    // interactive above read-only HeaderOne overlays.
    z-index: 2;
    display: flex;
    justify-content: center;
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => (height || '100dvh')};
    background: ${({ isSteering, theme }) => (isSteering ? theme.verascopeColor.orange300 : 'inherit')};
    cursor: ${({ customCursorActive, nativeCursor }) => (customCursorActive ? 'default' : nativeCursor)};
    transition: ${({ theme, transitionDuration }) => `background ${transitionDuration}ms ${theme.motion.standardEasing}`};

    &:hover {
        & > ${Handlebar} {
            background: ${({ theme, isSteering }) => (isSteering
        ? theme.verascopeColor.orange200
        : theme.verascopeColor.purple400)};
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        background: none;
        width: 0px;

        &:hover {
            background: none;

            & > ${Handlebar} {
                background: none;
            }
        }
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        background: none;
        width: 0px;

        &:hover {
            background: none;

            & > ${Handlebar} {
                background: none;
            }
        }
    }
`;

interface PurchaseBookButtonContainerProps {
    buttonHeight: number,
}
export const PurchaseBookButtonContainer = styled.div<PurchaseBookButtonContainerProps>`
    position: absolute;
    display: flex;
    right: ${`${11.5 * BODY_FONT_SIZE}px`};
    top: ${({ buttonHeight }) => `${-(buttonHeight + 0.5 * BODY_FONT_SIZE)}px`};

    ${({ theme }) => theme.mediaQuery.small} {
        left: 0;
        right: 0;
        width: 100%;
        justify-content: center;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        left: 0;
        right: 0;
        width: 100%;
        justify-content: center;
    }
`;

export const PostBannerPurchaseActions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: ${`${POST_BANNER_PURCHASE_ACTION_GAP}px`};
    flex-wrap: nowrap;
    max-width: 100%;

    & > button {
        flex: 0 0 auto;
    }

    ${({ theme }) => theme.mediaQuery.small} {
        justify-content: center;
        flex-wrap: wrap;
        max-width: 100%;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        justify-content: center;
        flex-wrap: wrap;
        max-width: 100%;
    }
`;

interface StickyPurchaseCtaContainerProps {
    animatedHeight: number,
    transitionDuration: number,
}
export const StickyPurchaseCtaContainer = styled.div<StickyPurchaseCtaContainerProps>`
    position: fixed;
    left: 50%;
    bottom: ${`${1 * BODY_FONT_SIZE}px`};
    transform: translateX(-50%);
    width: min(${`${STICKY_PURCHASE_CTA_MAX_WIDTH}px`}, calc(100% - ${2 * BODY_FONT_SIZE}px));
    height: ${({ animatedHeight }) => `${animatedHeight}px`};
    z-index: ${STICKY_PURCHASE_CTA_Z_INDEX};
    background: ${({ theme }) => theme.verascopeColor.purple100};
    border-radius: ${`${0.625 * BODY_FONT_SIZE}px`};
    box-shadow: ${({ theme }) => theme.color.boxShadow500};
    overflow: visible;
    transition: ${({ transitionDuration, theme }) => `
        height ${transitionDuration}ms ${theme.motion.overshoot}
    `};
`;

interface StickyPurchaseChevronAnchorProps {
    expanded: boolean,
}
export const StickyPurchaseChevronAnchor = styled.div<StickyPurchaseChevronAnchorProps>`
    position: absolute;
    right: ${`${0.6 * BODY_FONT_SIZE}px`};
    bottom: ${`${0.6 * BODY_FONT_SIZE}px`};
    z-index: 2;

    & svg {
        position: relative;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    &:hover path {
        fill: ${({ theme }) => theme.color.white};
    }

    & path {
        fill: ${({ theme }) => theme.verascopeColor.purple500};
    }

    transition: fill 0.3s;
`;

interface StickyPurchaseHeaderProps {
    centerContent: boolean,
}
export const StickyPurchaseHeader = styled.div<StickyPurchaseHeaderProps>`
    position: relative;
    display: flex;
    justify-content: ${({ centerContent }) => (centerContent ? 'center' : 'flex-start')};
    align-items: flex-end;
    gap: ${({ centerContent }) => (centerContent ? '0px' : `${1 * BODY_FONT_SIZE}px`)};
    padding: ${`${0.6 * BODY_FONT_SIZE}px`};
    padding-right: ${({ centerContent }) => (centerContent
        ? `${0.6 * BODY_FONT_SIZE}px`
        : `${3 * BODY_FONT_SIZE}px`
    )};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        justify-content: center;
        align-items: center;
        padding-right: ${`${0.6 * BODY_FONT_SIZE}px`};
    }
`;

interface StickyPurchaseHeaderButtonContainerProps {
    marginBottom: number,
}
export const StickyPurchaseHeaderButtonContainer = styled.div<StickyPurchaseHeaderButtonContainerProps>`
    display: flex;
    margin-bottom: ${({ marginBottom }) => `${marginBottom}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: auto;
        justify-content: center;
        gap: ${`${0.8 * DEFAULT_FONT_SIZE}px`};
    }
`;

interface StickyPurchaseHeaderCopyProps {
    hidden: boolean,
    transitionDuration: number,
}
export const StickyPurchaseHeaderCopy = styled.div<StickyPurchaseHeaderCopyProps>`
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
    align-self: center;
    flex: 1 1 auto;
    max-width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding-right: ${`${0.6 * BODY_FONT_SIZE}px`};
    min-width: 0;
    opacity: ${({ hidden }) => (hidden ? 0 : 1)};
    transform: ${({ hidden }) => (hidden ? 'translateY(-0.15rem)' : 'translateY(0)')};
    transition: ${({ transitionDuration }) => `
        opacity ${transitionDuration}ms,
        transform ${transitionDuration}ms
    `};
    pointer-events: ${({ hidden }) => (hidden ? 'none' : 'auto')};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        display: none;
        opacity: 0;
        pointer-events: none;
    }
`;

export const StickyPurchaseHeaderTitle = styled.div`
    display: block;
    width: 100%;
    max-width: 100%;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.875 * BODY_FONT_SIZE}px`};
    font-weight: 800;
    color: ${({ theme }) => theme.color.white};
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const StickyPurchaseHeaderTitleEmphasis = styled.span`
    font-style: italic;
    margin-left: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

export const StickyPurchaseHeaderSubtitle = styled.div`
    margin-top: ${`${0.2 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.6875 * BODY_FONT_SIZE}px`};
    font-weight: 400;
    color: ${({ theme }) => theme.color.cream400};
    line-height: 1.25;
`;

export const StickyPurchaseBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${`${0.5 * BODY_FONT_SIZE}px`};
    padding: ${`0 ${0.6 * BODY_FONT_SIZE}px ${0.75 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${0.6 * BODY_FONT_SIZE}px`};
    }
`;

interface StickyPurchaseContentContainerProps {
    visible: boolean,
}
export const StickyPurchaseContentContainer = styled.div<StickyPurchaseContentContainerProps>`
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
`;

export const StickyPurchaseChoiceRow = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.small} {
        flex-direction: column;
        align-items: center;
        gap: ${`${0.5 * BODY_FONT_SIZE}px`};

        &:not(:last-child) {
            margin-bottom: ${`${0.4 * BODY_FONT_SIZE}px`};
        }
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex-direction: column;
        align-items: center;
        gap: ${`${0.5 * BODY_FONT_SIZE}px`};

        &:not(:last-child) {
            margin-bottom: ${`${0.4 * BODY_FONT_SIZE}px`};
        }
    }
`;

export const StickyPurchaseChoiceCopy = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.64 * BODY_FONT_SIZE}px`};
    font-weight: 500;
    line-height: 1.35;
    color: ${({ theme }) => theme.color.cream400};
    min-height: calc(2 * 1.35em);

    ${({ theme }) => theme.mediaQuery.small} {
        display: block;
        font-size: ${`${0.68 * BODY_FONT_SIZE}px`};
        line-height: 1.25;
        text-align: center;
        max-width: ${`${20 * BODY_FONT_SIZE}px`};
        min-height: auto;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        display: block;
        font-size: ${`${0.68 * BODY_FONT_SIZE}px`};
        line-height: 1.25;
        text-align: center;
        max-width: ${`${20 * BODY_FONT_SIZE}px`};
        min-height: auto;
    }
`;

export const StickyPurchaseChoiceColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

interface StickyPurchaseRowContainerProps {
    withBottomMargin: boolean,
    marginBottom: number,
}
export const StickyPurchaseRowContainer = styled.div<StickyPurchaseRowContainerProps>`
    margin-bottom: ${({ withBottomMargin, marginBottom }) => (withBottomMargin ? `${marginBottom}px` : '0px')};
`;

export const InlineActionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
    flex-wrap: wrap;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.small} {
        flex-direction: column;
        align-items: center;
        gap: ${`${0.5 * BODY_FONT_SIZE}px`};
        margin-top: ${`${0.35 * BODY_FONT_SIZE}px`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex-direction: column;
        align-items: center;
        gap: ${`${0.5 * BODY_FONT_SIZE}px`};
        margin-top: ${`${0.35 * BODY_FONT_SIZE}px`};
    }
`;

export const StickyPurchaseMoreOptionsChevronContainer = styled.div`
    position: absolute;
    left: ${`${0.4 * BODY_FONT_SIZE}px`};
    top: 0;
    bottom: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${`${0.9 * BODY_FONT_SIZE}px`};
`;

interface StickyPurchaseMoreOptionsButtonContainerProps {
    width: number,
    height: number,
}
export const StickyPurchaseMoreOptionsButtonContainer = styled.div<StickyPurchaseMoreOptionsButtonContainerProps>`
    position: relative;
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};

    &:hover button {
        background: ${({ theme }) => theme.verascopeColor.purple200} !important;
    }
`;

export const HaggleButtonContainer = styled.div`
    &:hover button {
        background: ${({ theme }) => theme.verascopeColor.purple200} !important;
    }
`;

interface StickyPurchaseMoreOptionsChevronIconProps {
    expanded: boolean,
    transitionDuration: number,
}
export const StickyPurchaseMoreOptionsChevronIcon = styled.div<StickyPurchaseMoreOptionsChevronIconProps>`
    width: ${`${0.9 * BODY_FONT_SIZE}px`};
    height: ${`${0.9 * BODY_FONT_SIZE}px`};
    transform: ${({ expanded }) => `rotate(${expanded ? 90 : -90}deg)`};
    transition: ${({ transitionDuration }) => `transform ${transitionDuration}ms`};
    display: flex;
    align-items: center;
    justify-content: center;

    & svg, & div {
        position: relative;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    & path {
        fill: ${({ theme }) => theme.verascopeColor.purple500};
    }
`;

export const ButtonContainer = styled.div`
    position: relative;
`;

export const ModalContentsContainer = styled.div`
    position: relative;
`;

export const BookAboutDialogCloseButtonContainer = styled.div`
    position: absolute;
    top: ${`${-0.5 * BODY_FONT_SIZE}px`};
    right: ${`${-0.5 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.verascopeColor.purple200};
    border-radius: ${`${1.25 * BODY_FONT_SIZE}px`};
`;

export const ModalHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
`;

export const ModalTitle = styled.div`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1.8em;
    font-weight: 700;
    color: ${({ theme }) => theme.color.white};
    text-align: center;
    margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 1.5em;
    }  
`;

export const ModalSubtitle = styled.div`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1em;
    font-weight: 400;
    color: ${({ theme }) => theme.color.neutral600};
    line-height: 1.5em;
    text-align: center;
    max-width: 40ch;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.9em;
    }  
`;

export const ModalBody = styled.div`
    text-align: center;
`;

export const BookAboutDialogBody = styled.div`
    display: flex;
    flex-direction: column;
    max-height: min(56vh, ${`${31.25 * BODY_FONT_SIZE}px`});
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding-right: ${`${0.5 * BODY_FONT_SIZE}px`};
    text-align: left;
`;

export const BookAboutDialogParagraph = styled.p`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.95em;
    font-weight: 400;
    line-height: 1.65;
    color: ${({ theme }) => theme.color.cream400};
    margin: 0px;

    &:not(:last-child) {
        margin-bottom: ${`${1 * BODY_FONT_SIZE}px`};
    }
`;

interface AdventureCharacterAvatarContainerProps {
    background: string,
    transitionDuration: number,
}
export const AdventureCharacterAvatarContainer = styled.div<AdventureCharacterAvatarContainerProps>`
    display: flex;
    justify-content: center;
    position: relative;
    width: ${`${7.5 * BODY_FONT_SIZE}px`};
    height: ${`${7.5 * BODY_FONT_SIZE}px`};
    border-radius: ${`${3.75 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    overflow: hidden;
    box-shadow: ${({ theme }) => theme.color.insetBoxShadow200};
    transition: ${({
        transitionDuration,
        theme,
    }) => `
        background ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
`;

interface AdventureCharacterZoomLevelProps {
    color: string,
    transitionDuration: number,
}
export const AdventureCharacterZoomLevel = styled.h4<AdventureCharacterZoomLevelProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.8em;
    font-weight: 700;
    color: ${({ color }) => color};
    white-space: nowrap;
    margin: 0px;
    transition: ${({
        transitionDuration,
        theme,
    }) => `
        color ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
`;

interface AdventureCharacterContainerProps {
    background: string,
    zoomHoverColor: string,
    transitionDuration: number,
}
export const AdventureCharacterContainer = styled.div<AdventureCharacterContainerProps>`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    width: ${`${13.125 * BODY_FONT_SIZE}px`};
    height: ${`${13.125 * BODY_FONT_SIZE}px`};
    background: ${({ background }) => background};
    transform: scale(1);
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    padding: ${`${0.9375 * BODY_FONT_SIZE}px`};
    margin: ${`${0.3125 * BODY_FONT_SIZE}px`};
    transition: ${({
        transitionDuration,
        theme,
    }) => `
        background ${transitionDuration}ms ${theme.motion.eagerEasing},
        height ${transitionDuration}ms ${theme.motion.eagerEasing},
        box-shadow ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    &:hover {
        background: ${({ theme }) => theme.color.white};
        transform: scale(1.1);
        box-shadow: ${({ theme }) => theme.color.boxShadow300};
        z-index: 1;

        & > ${AdventureCharacterAvatarContainer} {
            background: ${({ background }) => background};
        }

        & > ${AdventureCharacterZoomLevel} {
            color: ${({ zoomHoverColor }) => zoomHoverColor};
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
    }   
`;

export const AdventureCharacterAvatar = styled.img`
    position: absolute;
    top: ${`${1.25 * BODY_FONT_SIZE}px`};
    width: 80%;
    height: 80%;
    pointer-events: none;
`;

interface AdventureCharacterNameProps {
    color: string,
}
export const AdventureCharacterName = styled.h2<AdventureCharacterNameProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1em;
    font-weight: 700;
    color: ${({ color }) => color};
    white-space: nowrap;
    margin: 0px;
    margin-top: ${`${0.9375 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
`;

interface CartAnimationItemsContainerProps {
    $portalMode?: boolean,
    $portalZIndex?: number,
}
export const CartAnimationItemsContainer = styled.div<CartAnimationItemsContainerProps>`
    position: ${({ $portalMode }) => ($portalMode ? 'static' : 'absolute')};
    top: ${({ $portalMode }) => ($portalMode ? 'auto' : '50%')};
    left: ${({ $portalMode }) => ($portalMode ? 'auto' : '50%')};
    transform: ${({ $portalMode }) => ($portalMode ? 'none' : 'translate(-50%, -50%)')};
    pointer-events: ${({ $portalMode }) => ($portalMode ? 'none' : 'auto')};
    z-index: ${({ $portalMode, $portalZIndex }) => ($portalMode
        ? ($portalZIndex ?? STICKY_PURCHASE_CTA_Z_INDEX)
        : STICKY_PURCHASE_CTA_Z_INDEX)};
`;

interface PaywallContainerProps {
    width: number,
}
export const PaywallContainer = styled.div<PaywallContainerProps>`
    position: relative;
    z-index: ${POST_PAYWALL_Z_INDEX};
    width: ${({ width }) => `${width}px`};
    background: ${({ theme }) => theme.color.cream100};
    box-shadow: ${({ theme }) => theme.color.boxShadow400};
`;

interface PaywallOverhangProps {
    extraOverhang?: number,
}
export const PaywallOverhang = styled.div<PaywallOverhangProps>`
    position: absolute;
    top: ${({ extraOverhang }) => (extraOverhang
        ? `-${1.5 * BODY_FONT_SIZE + extraOverhang}px`
        : `-${1.5 * BODY_FONT_SIZE}px`
    )};
    left: 0;
    width: 100%;
    height: ${({ extraOverhang }) => (extraOverhang
        ? `${POST_PAYWALL_OVERHANG_HEIGHT + extraOverhang}px`
        : `${POST_PAYWALL_OVERHANG_HEIGHT}px`)};
    background: ${({ theme }) => theme.color.cream100};
    border-radius: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

export const PaywallContent = styled.div`
    position: relative;
    top: -35vh;
`;

export const PaywallHeader = styled.h2`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 2em;
    font-weight: 700;
    color: ${({ theme }) => theme.color.white};
    line-height: 1.5em;
    text-align: center;
    padding: ${`0px ${1.5 * BODY_FONT_SIZE}px`};
    margin: ${`${2.5 * BODY_FONT_SIZE}px 0px`};
    margin-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
    white-space: nowrap;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 1.7em;
    }
    
    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 1.5em;
    }
`;

export const PaywallSubheader = styled.h3`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1.1em;
    font-weight: 500;
    color: ${({ theme }) => theme.color.cream400};
    line-height: 1.5em;
    max-width: 30ch;
    text-align: center;
    padding: ${`0px ${1.5 * BODY_FONT_SIZE}px`};
    margin: 0px auto;
    margin-bottom: ${`${2 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.9em;
    }
`;

export const PaywallButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin: 0px auto;
`;

export const AuthLockedContainer = styled(PaywallContainer)`
`;

export const AuthLockedActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
`;

export const AuthLockedEmailRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${`${0.5 * BODY_FONT_SIZE}px`};
    width: 100%;
`;

interface AuthLockedButtonContainerProps {
    buttonHeight: number,
    $loading: boolean,
}
export const AuthLockedButtonContainer = styled.div<AuthLockedButtonContainerProps>`
    & button section {
        width: 100%;
        align-items: center;
        ${({ $loading, buttonHeight }) => ($loading
        ? `
            display: flex;
            justify-content: center;
        `
        : `
            display: grid;
            grid-template-columns: ${BUTTON_ICON_MULTIPLIER * buttonHeight}px 1fr;
        `
    )};
    }

    ${({ $loading }) => !$loading && `
        & button section > div:first-child {
            grid-column: 1;
            justify-self: center;
        }

        & button section > h3 {
            grid-column: 2;
            justify-self: center;
        }
    `};
`;

interface LockedContainerProps {
    height: number,
}
export const LockedContainer = styled(PaywallContainer)<LockedContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: ${({ height }) => `${height}px`};
    border-radius: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

export const LockedIcon = styled.div`
    position: relative;
    height: ${`${5 * BODY_FONT_SIZE}px`};
    width: ${`${5 * BODY_FONT_SIZE}px`};
    
    & svg, & div {
        position: relative;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    & path {
        fill: ${({ theme }) => theme.color.cream300};
    }
`;

export const LockedHeader = styled(PaywallHeader)`
    margin: ${`${0.5 * BODY_FONT_SIZE}px 0px`};
`;

export const LockedSubheader = styled(PaywallSubheader)`
`;

export const VersionHistoryButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const CollaborativePresenceContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const CollaborativePresenceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${`${0.375 * BODY_FONT_SIZE}px`};
`;

export const CollaborativePresenceAvatarWrapper = styled.div`
    position: relative;
    width: ${`${1.625 * BODY_FONT_SIZE}px`};
    height: ${`${1.625 * BODY_FONT_SIZE}px`};
`;

export const CollaborativePresenceAvatarButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${`${1.625 * BODY_FONT_SIZE}px`};
    height: ${`${1.625 * BODY_FONT_SIZE}px`};
    padding: 0;
    border: 1px solid ${({ theme }) => theme.verascopeColor.purple200};
    border-radius: 50%;
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.color.boxShadow200};
    overflow: hidden;

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.verascopeColor.orange200};
        outline-offset: 2px;
    }
`;

export const CollaborativePresenceInitials = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.verascopeColor.purple400};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.75 * BODY_FONT_SIZE}px`};
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: uppercase;
`;

export const EditorStatusRowContainer = styled.div`
    position: fixed;
    right: ${`${2.5 * BODY_FONT_SIZE}px`};
    bottom: ${`${2.1875 * BODY_FONT_SIZE}px`};
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
    z-index: ${POST_HISTORY_BUTTON_Z_INDEX};
`;

export const EditorStatusControlsRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
`;

/**
 * Touch-mobile annotation editors render inside a dedicated fixed overlay so
 * the docked editor stays pinned to the viewport instead of riding page scroll
 * while the keyboard is open.
 */
export const DockedAnnotationEditorViewportRoot = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 1000;
`;

export const MobileDetailSheetRoot = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
`;

export const MobileDetailSheetBackdrop = styled.div`
    position: absolute;
    inset: 0;
    background-color: rgba(
        ${({ theme }) => hexToRgb(theme.verascopeColor.purple100)!.r},
        ${({ theme }) => hexToRgb(theme.verascopeColor.purple100)!.g},
        ${({ theme }) => hexToRgb(theme.verascopeColor.purple100)!.b},
        0.6
    );
`;

export const MobileDetailSheetPanel = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(100% - ${DETAIL_SHEET_HEIGHT_REDUCTION}px);
    background: inherit;
    box-shadow: ${({ theme }) => theme.color.boxShadow300};
    border-top-left-radius: ${0.625 * BODY_FONT_SIZE}px;
    border-top-right-radius: ${0.625 * BODY_FONT_SIZE}px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
`;
